import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROLES, ROLE_PERMISSIONS, hasPermission, hasAnyPermission } from '../../config/roles';

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_CONSOLE_API}/console/v1/auth/login`,
        {
          email: credentials.email,
          password: credentials.password,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_CONSOLE_API_KEY,
          },
        }
      );

      const data = response.data;

      // Extract token from response
      const token = data.jwtToken;

      // Map user info from API response
      const role = data.user?.role || ROLES.SUPER_ADMIN;

      return {
        user: {
          id: data.user?.userId || 'usr-001',
          email: data.user?.email || credentials.email,
          name: data.user?.name || credentials.email?.split('@')[0] || 'Admin User',
          role,
          permissions: data.user?.permissions || [],
          persona: data.user?.persona || '',
          tenantId: data.user?.tenantId || [],
        },
        token,
        refreshToken: data.refreshToken || null,
        expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Login failed. Please check your credentials.' }
      );
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

// Rehydrate auth state from localStorage on app load
const persistedToken = localStorage.getItem('token');
const persistedUser = localStorage.getItem('authUser');

const initialState = {
  user: persistedUser ? JSON.parse(persistedUser) : null,
  token: persistedToken || null,
  expiresAt: null,
  isAuthenticated: !!(persistedToken && persistedUser),
  loading: false,
  error: null,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.expiresAt = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('token');
      localStorage.removeItem('authUser');
    },
    clearAuthError(state) {
      state.error = null;
    },
    setAuth(state, action) {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.expiresAt = action.payload.expiresAt;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.expiresAt = action.payload.expiresAt;
        state.isAuthenticated = true;
        // Persist to localStorage
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
        if (action.payload.user) {
          localStorage.setItem('authUser', JSON.stringify(action.payload.user));
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Login failed';
      });
  },
});

export const { logout, clearAuthError, setAuth } = authSlice.actions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectUserRole = (state) => state.auth.user?.role || null;
export const selectHasPermission = (permission) => (state) => {
  const role = state.auth.user?.role;
  return role ? hasPermission(role, permission) : false;
};
export const selectHasAnyPermission = (permissions) => (state) => {
  const role = state.auth.user?.role;
  return role ? hasAnyPermission(role, permissions) : false;
};

export default authSlice.reducer;
